  <!-- The template contains the HTML of our module  -->
  <template>
    <!--
    Our module builds upon the 'wgu-module-card'
    it handles the integration into Wegue
    -->
    <wgu-module-card
      v-bind="$attrs"
      moduleName="ids-detail-filter-module"
      class="ids-detail-filter"
      :icon="icon"
    >

      <!-- year number fields -->
      <v-container
        fluid
      >

      <v-card-subtitle class="pl-0 pt-0 pb-1">{{ $t('ids-detail-filter-module.description') }}</v-card-subtitle>
      <!-- <v-card-subtitle class="pl-0 pt-0 pb-1">Filtern Sie die aktuellen Ortsdaten auf folgende Jahre:</v-card-subtitle> -->
        <v-row no-gutters>

          <v-col cols="5">
            <v-text-field
              :value="filterInterval[0]"
              class="mt-0 pt-0 ids-year-input"
              hide-details
              single-line
              type="number"
              full-width
              @input="onLowerInputText"
              :rules="[
                (ly) => !!ly && ly >= minYear && ly <= maxYear
              ]"
            ></v-text-field>
          </v-col>

          <v-col cols="5">
            <v-text-field
              :value="filterInterval[1]"
              class="mt-0 pt-0 ml-1 ids-year-input"
              hide-details
              single-line
              type="number"
              full-width
              @input="onUpperInputText"
              :rules="[
                (uy) => !!uy && uy >= minYear && uy <= maxYear
              ]"
            ></v-text-field>
          </v-col>

          <!-- Reset button -->
          <v-col cols="2" class="d-flex justify-center align-center">
            <v-btn
              small
              elevation="0"
              title="Zeitraum zurücksetzen"
              @click="resetFilterInterval"
            >
              <v-icon >mdi-reload</v-icon>
            </v-btn>
          </v-col>

        </v-row>

      </v-container>
      <!-- END year number fields -->

      <v-range-slider
        v-model="filterInterval"
        :max="maxYear"
        :min="minYear"
        hide-details
        class="mr-2 ml-2 align-center"
      >
      </v-range-slider>

    </wgu-module-card>
</template>

<script>
  import ModuleCard from '../../../../src/components/modulecore/ModuleCard';
  import { Mapable } from '../../../../src/mixins/Mapable';
  import { WguEventBus } from '../../../../src/WguEventBus';
  import LayerUtil from '../../../../src/util/Layer';
  import JsUtil from '../../../util/JsUtil';

  export default {
    name: 'ids-detail-filter-module-win',
    mixins: [Mapable],
    inheritAttrs: false,
    components: {
      'wgu-module-card': ModuleCard
    },
    props: {
      icon: { type: String, required: false, default: 'filter' },
      filterMinYear: { type: Number, required: false },
      filterMaxYear: { type: Number, required: false },
      filterYearAttribute: { type: String, required: false, default: 'Aufnahmejahr' }
    },
    data () {
      return {
        filterInterval: [1900, 2000],
        minYear: null,
        maxYear: null,
        lastLowerYear: null,
        lastUpperYear: null,
        allLocationFeatures: null, // lookup for all locations currently loaded
        yearAttribute: null,
        initialFilter: true
      };
    },
    created () {
      // apply values from module config (see app-conf.json)

      // min / max limits of slider
      this.minYear = JsUtil.isNumeric(this.filterMinYear)
        ? parseInt(this.filterMinYear) : this.filterInterval[0];
      this.maxYear = JsUtil.isNumeric(this.filterMaxYear)
        ? parseInt(this.filterMaxYear) : new Date().getFullYear();

      // set initial filter interval
      this.filterInterval = [this.minYear, this.maxYear];

      // property name/key of the year attribute
      this.yearAttribute = this.filterYearAttribute;
    },
    watch: {
      filterInterval (rangeVals) {
        const lowerYear = parseInt(rangeVals[0], 10);
        const upperYear = parseInt(rangeVals[1], 10);

        // check if initial locations are already loaded
        const locationLayer = LayerUtil.getLayerByLid('ids-result-layer', this.map);
        const clusterSource = locationLayer.getSource();
        const locationSource = clusterSource.getSource();
        if (this.initialFilter && locationSource.getFeatures().length === 0) {
          return; // skip initial run when creating this UI
        }

        // only apply new filter if values changed
        if (this.lastLowerYear !== lowerYear || this.lastUpperYear !== upperYear) {
          this.filterLocationData(lowerYear, upperYear);
        }
        // preserve old filter filterInterval
        this.lastLowerYear = lowerYear;
        this.lastUpperYear = upperYear;

        this.initialFilter = false; // first initial run is done
      }
    },
    methods: {

      onMapBound () {
        // reset all location items if new locations were load from backend
        WguEventBus.$on('ids-new-locations-load', () => {
          // reset all feature lookup
          this.allLocationFeatures = null;
          // reset filter to maximum interval
          this.filterInterval = [this.minYear, this.maxYear];
        });
      },

      /**
       * Handles the 'input' event of the lower time range limit.
       *
       * @param {String} inputLowerYear the new input val for lower filter year
       */
      onLowerInputText (inputLowerYear) {
        const lowerYear = parseInt(inputLowerYear, 10);
        // check if in mix/max range of filter interval
        if (lowerYear >= this.minYear && lowerYear <= this.maxYear) {
          this.filterInterval = [lowerYear, this.filterInterval[1]];
        }
      },

      /**
       * Handles the 'input' event of the upper time range limit.
       *
       * @param {String} inputUpperYear the new input val for upper filter year
       */
      onUpperInputText (inputUpperYear) {
        const upperYear = parseInt(inputUpperYear, 10);
        // check if in mix/max range of filter interval
        if (upperYear >= this.minYear && upperYear <= this.maxYear) {
          this.filterInterval = [this.filterInterval[0], upperYear];
        }
      },

      /**
       * Filters the location data on the map by the given years.
       *
       * @param {Number} lowerYear The lower year boundary
       * @param {Number} upperYear The upper year boundary
       */
      filterLocationData (lowerYear, upperYear) {
        const locationLayer = LayerUtil.getLayerByLid('ids-result-layer', this.map);
        const clusterSource = locationLayer.getSource();
        const locationSource = clusterSource.getSource();

        if (!this.allLocationFeatures) {
          this.allLocationFeatures = locationSource.getFeatures();
        }
        // check if we have a dataset with time filter property
        // we assume if the first feature doesn't have the whole dataset doesn't
        const firstFeat = this.allLocationFeatures[0];
        if (!this.featuresHaveFilterProp(firstFeat)) {
          return;
        };

        const filteredFeatures = this.allLocationFeatures.filter((subFeat) => {
          const year = subFeat.get(this.yearAttribute);
          if (!JsUtil.isNumeric(year)) {
            return false; // filter out data with no attribute for a year
          }

          return year >= lowerYear && year <= upperYear;
        });

        // reset the old data with newly filtered locations
        locationSource.clear();
        locationSource.addFeatures(filteredFeatures);
      },

      /**
       * Resets the filter to the initial min/max values
       */
      resetFilterInterval () {
        // set initial filter interval
        this.filterInterval = [this.minYear, this.maxYear];
      },

      /**
       * Checks if the given feature has a time filter property defined in
       * #yearAttribute
       */
      featuresHaveFilterProp (feat) {
        return feat && JsUtil.isNumeric(feat.get(this.yearAttribute))
      }
    }
  };
</script>

<style>

    /* place detail filter module at the bottom of sidebar */
    .v-card.ids-detail-filter {
      position: absolute;
      bottom: 0;
    }

</style>



// WEBPACK FOOTER //
// app/ids/components/detailfilter/IdsDetailFilterModuleWin.vue